import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { addConsumerTopicInterest, getErrorMessage } from "../api/userAuthApi";
import { addUserInterest, setUser } from "../store/slices/userSlice";
import showToast from "../utils/toast";
import {
  resolveTopicBackendIds,
  userHasInterestForTopic,
} from "../utils/resolveTopicBackendIds";
import { ENGAGEMENT_SUBSCRIBE_DWELL_MS } from "./useEngagementSubscribePrompt";

function sessionUnlockKey(topicIds) {
  if (!topicIds) return "";
  return `ibx-topic-interest:${topicIds.categoryId}:${topicIds.subCategoryId}`;
}

/**
 * After the user has browsed for a few minutes and scrolled, prompts logged-in visitors
 * who have not chosen this topic at sign-up to add it or return to the industry hub.
 */
export function useSubcategoryInterestGate({
  isAuthenticated,
  user,
  signupCategories,
  industryId,
  industryLabel,
  menuItem,
  slug,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const gateConsumedRef = useRef(false);
  const landedAtRef = useRef(Date.now());
  const topicIds = useMemo(
    () => resolveTopicBackendIds(signupCategories, industryId, menuItem),
    [signupCategories, industryId, menuItem],
  );

  const alreadyUnlockedSession = useMemo(() => {
    if (typeof window === "undefined" || !topicIds) return false;
    try {
      return Boolean(window.sessionStorage.getItem(sessionUnlockKey(topicIds)));
    } catch {
      return false;
    }
  }, [topicIds]);

  const shouldOfferGate = useMemo(() => {
    if (!isAuthenticated || !user || !menuItem || !topicIds) return false;
    if (alreadyUnlockedSession) return false;
    return !userHasInterestForTopic(user, topicIds);
  }, [isAuthenticated, user, menuItem, topicIds, alreadyUnlockedSession]);

  useEffect(() => {
    gateConsumedRef.current = false;
    landedAtRef.current = Date.now();
  }, [industryId, slug]);

  useEffect(() => {
    if (!shouldOfferGate || gateConsumedRef.current) return undefined;

    const considerOpen = () => {
      if (gateConsumedRef.current) return;
      const elapsed = Date.now() - landedAtRef.current;
      if (elapsed < ENGAGEMENT_SUBSCRIBE_DWELL_MS) return;
      gateConsumedRef.current = true;
      setOpen(true);
    };

    considerOpen();

    const interval = window.setInterval(considerOpen, 4_000);
    window.addEventListener("scroll", considerOpen, { passive: true });
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("scroll", considerOpen);
    };
  }, [shouldOfferGate, industryId, slug]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const onGoBack = useCallback(() => {
    setOpen(false);
    navigate(`/${industryId}`, { replace: false });
  }, [navigate, industryId]);

  const onProceed = useCallback(async () => {
    if (!topicIds || !user || pending) return;
    const uid = user.id ?? user.user_id;
    if (uid == null) {
      showToast.error("Could not read your account. Please sign in again.");
      return;
    }
    setPending(true);
    try {
      const result = await addConsumerTopicInterest({
        user_id: uid,
        category: topicIds.categoryName,
        sub_category: topicIds.subCategoryName,
      });

      try {
        window.sessionStorage.setItem(sessionUnlockKey(topicIds), "1");
      } catch {
        /* ignore */
      }

      if (result?.user && typeof result.user === "object" && !Array.isArray(result.user)) {
        dispatch(setUser(result.user));
      } else if (result?._stub) {
        dispatch(
          addUserInterest({
            id: `local-${Date.now()}`,
            user_id: user.id,
            category_id: topicIds.categoryId,
            sub_category_id: topicIds.subCategoryId,
            category: {
              id: topicIds.categoryId,
              name: topicIds.categoryName,
            },
            sub_category: {
              id: topicIds.subCategoryId,
              name: topicIds.subCategoryName,
              category_id: topicIds.categoryId,
            },
          }),
        );
      }

      const toastMsg =
        typeof result?.reason === "string" && result.reason.trim()
          ? result.reason.trim()
          : "You're subscribed to this topic — we'll tailor updates for you here.";
      showToast.success(toastMsg);
      setOpen(false);
    } catch (err) {
      const msg = getErrorMessage(err);
      showToast.error(typeof msg === "string" ? msg : "Could not add this topic. Try again.");
    } finally {
      setPending(false);
    }
  }, [topicIds, user, pending, dispatch]);

  return {
    open,
    setOpen,
    shouldOfferGate,
    topicIds,
    onProceed,
    onGoBack,
    pending,
    industryLabel: industryLabel || "",
    topicLabel: menuItem?.label || menuItem?.name || "",
  };
}
